import { styled } from "@mui/material/styles";
import { DataGrid } from "../../../components/DataGrid";
import { useGridPagination } from "../hooks/useGridPagination";
import { useCommercialGridFeed } from "../hooks/useCommercialGridFeed";
import { useCommercialMaskUnlockFlow } from "../hooks/useCommercialMaskUnlockFlow";
import { makeCommercialRegistrationReceptionBookDataGridColumns } from "../utils";
import { toCommercialRegistrationNumber } from "../utils/feedUtils";
import { CustomToolbar } from "./CustomToolbar";
import {
  type ICommercialFeedFetchParams,
  type ICommercialFeedDataGridRow,
  type CommercialReceptionBookViewPlan,
} from "../types";
import type { ReceptionBookBulkGridSelection } from "@/features/commercialBook/types";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { type GridApiPro } from "@mui/x-data-grid-pro/models/gridApiPro";
import {
  type GridColDef,
  type GridRowParams,
  type GridSelectionModel,
} from "@mui/x-data-grid-pro";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { type User } from "@/types/localstorage";
import { MaskUnlockConfirmDialog } from "./MaskUnlockConfirmDialog";

interface IProps {
  apiRef: React.MutableRefObject<GridApiPro>;
  clearSelection: boolean;
  setClearSelection: React.Dispatch<React.SetStateAction<boolean>>;
  feedParams: ICommercialFeedFetchParams | null;
  plan: CommercialReceptionBookViewPlan;
  onReceptionBookBulkSelectionChange: (
    selection: ReceptionBookBulkGridSelection
  ) => void;
}

interface IFeedGridContentProps extends IProps {
  rows: ICommercialFeedDataGridRow[];
  allCount: number;
  loading: boolean;
  page: number;
  pageSize: number;
  changePage: (pageIndex: number) => void;
  changePageSize: (pageSize: number) => void;
  columns: GridColDef[];
  maskedRowIds: Set<number>;
  onMaskUnlock: (ids: number[]) => void;
  unlockDialog?: React.ReactNode;
}

const StyledDataGrid = styled(DataGrid)`
  border: 0;

  // テーブルヘッダーの背景色を設定
  .MuiDataGrid-columnHeaders {
    line-height: 1.5em !important;
  }

  // テーブルタイトルを太字に
  .MuiDataGrid-columnHeaderTitle {
    font-weight: 700;
  }

  // ページネーションの件数表示を太字に
  .MuiTablePagination-displayedRows {
    font-weight: 700;
  }

  .MuiTablePagination-selectIcon {
    color: #000;
  }
`;

const FeedGridContent: React.FC<IFeedGridContentProps> = (props) => {
  const {
    rows,
    allCount,
    loading,
    page,
    pageSize,
    changePage,
    changePageSize,
    columns,
    maskedRowIds,
    onMaskUnlock,
    unlockDialog,
    clearSelection,
    setClearSelection,
    plan,
    onReceptionBookBulkSelectionChange,
  } = props;

  // 選択行の管理（会社法人等番号による一括検索用）
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  // 親への一括選択通知の重複発火を防ぐため、前回通知した内容を保持するref
  const prevBulkSelectionKeyRef = useRef<string>("");

  // 選択行/rows が変化するたびに、選択された行の会社法人等番号を
  // ユニーク化して親に通知する。法人名はエラー表示用に保持するが API には送らない。
  // 受付帳には 13 桁の法人番号（先頭チェック桁 + 会社法人等番号 12 桁）で保存されているため、
  // 民事法務協会 API 仕様（12 桁の会社法人等番号）に合わせて末尾 12 桁を抽出する。
  useEffect(() => {
    const selectedIdSet = new Set<string>(
      selectionModel.map((id) => String(id))
    );
    const selectedRows = rows.filter((row: ICommercialFeedDataGridRow) =>
      selectedIdSet.has(String(row.id))
    );
    const corporateNumberDisplayNames: Record<string, string> = {};
    const corporateNumbers: string[] = [];
    const seenCn = new Set<string>();
    for (const row of selectedRows) {
      const cn = toCommercialRegistrationNumber(
        row.requesterCommercialRegistrationNumber
      );
      if (!cn) continue;
      const name = (row.requesterCorporateName ?? "").trim();
      if (!seenCn.has(cn)) {
        seenCn.add(cn);
        corporateNumbers.push(cn);
        corporateNumberDisplayNames[cn] = name;
      } else {
        corporateNumberDisplayNames[cn] =
          name || corporateNumberDisplayNames[cn] || "";
      }
    }
    const nextSelection: ReceptionBookBulkGridSelection = {
      corporateNumbers,
      corporateNumberDisplayNames,
    };
    // 通知内容が前回と変わっていない場合は親を再レンダーさせないよう通知をスキップする
    const nextSelectionKey = JSON.stringify(nextSelection);
    if (prevBulkSelectionKeyRef.current === nextSelectionKey) return;
    prevBulkSelectionKeyRef.current = nextSelectionKey;
    onReceptionBookBulkSelectionChange(nextSelection);
  }, [selectionModel, rows, onReceptionBookBulkSelectionChange]);

  // 会社法人等番号の値自体が存在しない行は、一括検索・一括閲覧のいずれの対象にもできないため選択不可にする。
  // 単票の閲覧ボタンは法人番号だけでも表示されるが（hasRequesterInfo）、ここは条件を広げない。
  // 一括検索（商業登記簿の一括取得）は12桁の会社法人等番号が必須のため、広げると
  // 「一括閲覧はできるが一括検索はできない行」が選択可能になってしまう。
  // なお法人番号は会社法人等番号から導出するため、法人番号だけが埋まる行は現状発生しない。
  const isRowSelectable = useCallback(
    (params: GridRowParams<ICommercialFeedDataGridRow>): boolean =>
      params.row.requesterCommercialRegistrationNumber.trim() !== "",
    []
  );

  const [user] = useLocalStorage<User | undefined>("user", undefined);
  const rowsPerPageOptions: number[] = [10, 50, 100];

  return (
    <>
      {unlockDialog}
      <StyledDataGrid
        stickyHeader={true}
        apiRef={props.apiRef}
        disableColumnFilter
        checkboxSelection
        keepNonExistentRowsSelected
        isRowSelectable={isRowSelectable}
        rows={rows}
        columns={columns}
        initialState={{
          columns: {
            columnVisibilityModel: {
              id: false,
            },
          },
        }}
        rowCount={allCount}
        pageSize={pageSize}
        page={page}
        loading={loading}
        paginationMode="client"
        sortingMode="client"
        rowsPerPageOptions={rowsPerPageOptions}
        onPageChange={changePage}
        onPageSizeChange={changePageSize}
        selectionModel={selectionModel}
        onSelectionModelChange={setSelectionModel}
        disableSelectionOnClick={true}
        headerHeight={100}
        getRowHeight={() => "auto" as const}
        components={{
          Toolbar: CustomToolbar,
        }}
        componentsProps={{
          toolbar: {
            loading,
            user,
            page,
            pageSize,
            allCount,
            rowsPerPageOptions,
            changePage,
            changePageSize,
            clearSelection,
            setClearSelection,
            plan,
            maskedRowIds,
            onMaskUnlock,
          },
        }}
      />
    </>
  );
};

const useFeedGridData = (
  feedParams: ICommercialFeedFetchParams | null
): {
  rows: ICommercialFeedDataGridRow[];
  allCount: number;
  loading: boolean;
  page: number;
  pageSize: number;
  changePage: (pageIndex: number) => void;
  changePageSize: (pageSize: number) => void;
} => {
  const { page, pageSize, changePage, changePageSize } = useGridPagination();
  const prevFeedParamsRef = useRef<string>("");

  // 検索条件が変更されたらページを0にリセット
  useEffect(() => {
    const currentFeedParamsKey = JSON.stringify(feedParams);
    if (
      prevFeedParamsRef.current !== "" &&
      prevFeedParamsRef.current !== currentFeedParamsKey
    ) {
      changePage(0);
    }
    prevFeedParamsRef.current = currentFeedParamsKey;
  }, [feedParams, changePage]);

  const { rows, allCount, loading } = useCommercialGridFeed(
    {
      page,
      pageSize,
      ...feedParams,
    },
    { enabled: feedParams !== null }
  );

  return {
    rows,
    allCount,
    loading,
    page,
    pageSize,
    changePage,
    changePageSize,
  };
};

const FlatMonthlyFeedGrid: React.FC<IProps> = (props) => {
  const { feedParams, plan } = props;
  const feedData = useFeedGridData(feedParams);

  const columns = useMemo(
    () => makeCommercialRegistrationReceptionBookDataGridColumns(plan),
    [plan]
  );

  return (
    <FeedGridContent
      {...props}
      {...feedData}
      columns={columns}
      maskedRowIds={new Set()}
      onMaskUnlock={() => undefined}
    />
  );
};

const PayPerViewFeedGrid: React.FC<IProps> = (props) => {
  const { feedParams, plan } = props;
  const feedData = useFeedGridData(feedParams);

  const {
    mergedRows,
    maskedRowIds,
    handleUnlockSingle,
    handleUnlockMultiple,
    unlockDialogProps,
  } = useCommercialMaskUnlockFlow(feedParams, feedData.rows);

  const columns = useMemo(
    () =>
      makeCommercialRegistrationReceptionBookDataGridColumns(
        plan,
        handleUnlockSingle
      ),
    [plan, handleUnlockSingle]
  );

  return (
    <FeedGridContent
      {...props}
      {...feedData}
      rows={mergedRows}
      columns={columns}
      maskedRowIds={maskedRowIds}
      onMaskUnlock={handleUnlockMultiple}
      unlockDialog={<MaskUnlockConfirmDialog {...unlockDialogProps} />}
    />
  );
};

export const FeedGrid: React.FC<IProps> = (props) => {
  if (props.plan === "flat_monthly") {
    return <FlatMonthlyFeedGrid {...props} />;
  }
  return <PayPerViewFeedGrid {...props} />;
};
